/**
 * Offline Cache Service - localStorage implementation
 *
 * Implements OfflineCacheService interface for browser-based offline caching (FR-036).
 * Handles storage quota exceeded scenarios, expiration, and cache invalidation.
 */

import type { OfflineCacheService, CacheStats } from '@hostel-manager/shared/repositories';
import {
  CACHE_EXPIRATION_MS,
  CACHE_STORAGE_LIMITS,
  CACHE_INVALIDATION,
} from '../config/cache-config';

const STORAGE_PREFIX = 'hm_cache_';
const META_PREFIX = 'hm_meta_';

interface CacheEntry<T> {
  data: T;
  expiresAt?: number;
  createdAt: number;
}

/**
 * LocalStorage implementation of OfflineCacheService.
 * Provides offline data persistence with expiration and quota management.
 */
export class LocalStorageOfflineCacheService implements OfflineCacheService {
  private readonly prefix = STORAGE_PREFIX;
  private readonly metaPrefix = META_PREFIX;

  set<T>(key: string, data: T, expirationMs?: number): void {
    const fullKey = this.prefix + key;
    const duration = expirationMs ?? CACHE_EXPIRATION_MS.default;
    const now = Date.now();
    const entry: CacheEntry<T> = {
      data,
      expiresAt: duration > 0 ? now + duration : undefined,
      createdAt: now,
    };

    const serialized = JSON.stringify(entry);
    try {
      this.checkStorageQuota(serialized.length, fullKey);
      localStorage.setItem(fullKey, serialized);
      this.updateMeta(key, serialized.length);
    } catch (error) {
      if (this.isQuotaExceededError(error)) {
        this.evictOldestEntries(serialized.length);
        try {
          localStorage.setItem(fullKey, JSON.stringify(entry));
          this.updateMeta(key, JSON.stringify(entry).length);
        } catch (retryError) {
          console.warn('[OfflineCache] Storage full, could not cache:', key, retryError);
        }
      } else {
        throw error;
      }
    }
  }

  get<T>(key: string): T | null {
    const fullKey = this.prefix + key;
    try {
      const raw = localStorage.getItem(fullKey);
      if (!raw) return null;

      const entry = JSON.parse(raw) as CacheEntry<T>;
      if (entry.expiresAt && Date.now() > entry.expiresAt) {
        this.remove(key);
        return null;
      }
      return entry.data as T;
    } catch {
      return null;
    }
  }

  remove(key: string): void {
    const fullKey = this.prefix + key;
    try {
      localStorage.removeItem(fullKey);
      localStorage.removeItem(this.metaPrefix + key);
    } catch {
      // Ignore removal errors
    }
  }

  clear(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(this.prefix) || key?.startsWith(this.metaPrefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch (error) {
      console.warn('[OfflineCache] Clear failed:', error);
    }
  }

  has(key: string): boolean {
    const fullKey = this.prefix + key;
    try {
      const raw = localStorage.getItem(fullKey);
      if (!raw) return false;
      const entry = JSON.parse(raw) as CacheEntry<unknown>;
      if (entry.expiresAt && Date.now() > entry.expiresAt) {
        this.remove(key);
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  isExpired(key: string): boolean {
    const fullKey = this.prefix + key;
    try {
      const raw = localStorage.getItem(fullKey);
      if (!raw) return true;
      const entry = JSON.parse(raw) as CacheEntry<unknown>;
      if (!entry.expiresAt) return false;
      return Date.now() > entry.expiresAt;
    } catch {
      return true;
    }
  }

  getStats(): CacheStats {
    let totalKeys = 0;
    let totalSize = 0;
    let expiredKeys = 0;
    const now = Date.now();

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key?.startsWith(this.prefix)) continue;

        const raw = localStorage.getItem(key);
        if (!raw) continue;

        totalKeys++;
        totalSize += new Blob([raw]).size;

        try {
          const entry = JSON.parse(raw) as CacheEntry<unknown>;
          if (entry.expiresAt && now > entry.expiresAt) expiredKeys++;
        } catch {
          expiredKeys++;
        }
      }

      // Estimate storage quota (typically 5-10MB)
      const estimatedQuota = 5 * 1024 * 1024;
      const storageQuotaUsed = Math.min(
        100,
        Math.round((totalSize / estimatedQuota) * 100)
      );

      return {
        totalKeys,
        totalSize,
        expiredKeys,
        storageQuotaUsed,
      };
    } catch {
      return { totalKeys: 0, totalSize: 0, expiredKeys: 0, storageQuotaUsed: 0 };
    }
  }

  /**
   * Evict expired entries to free space.
   * Call periodically or when quota is approached.
   */
  evictExpired(): void {
    const keysToRemove: string[] = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key?.startsWith(this.prefix)) continue;

        const shortKey = key.slice(this.prefix.length);
        if (CACHE_INVALIDATION.protectedKeys.includes(shortKey as 'offline_queue' | 'last_sync')) continue;

        if (this.isExpired(shortKey)) keysToRemove.push(shortKey);
      }
      keysToRemove.forEach((k) => this.remove(k));
    } catch (error) {
      console.warn('[OfflineCache] Evict expired failed:', error);
    }
  }

  private checkStorageQuota(additionalBytes: number, _currentKey: string): void {
    const stats = this.getStats();
    const projectedTotal = stats.totalSize + additionalBytes;
    const threshold = (CACHE_STORAGE_LIMITS.evictionThresholdPercent / 100) * CACHE_STORAGE_LIMITS.maxTotalSizeBytes;

    if (projectedTotal > threshold) {
      this.evictOldestEntries(additionalBytes);
    }
  }

  private evictOldestEntries(requiredBytes: number): void {
    const entries: { key: string; createdAt: number; size: number }[] = [];

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key?.startsWith(this.prefix)) continue;

        const shortKey = key.slice(this.prefix.length);
        if (CACHE_INVALIDATION.protectedKeys.includes(shortKey as 'offline_queue' | 'last_sync')) continue;
        if (this.isExpired(shortKey)) {
          this.remove(shortKey);
          continue;
        }

        const raw = localStorage.getItem(key);
        if (!raw) continue;

        try {
          const entry = JSON.parse(raw) as CacheEntry<unknown>;
          entries.push({
            key: shortKey,
            createdAt: entry.createdAt ?? 0,
            size: new Blob([raw]).size,
          });
        } catch {
          this.remove(shortKey);
        }
      }

      entries.sort((a, b) => a.createdAt - b.createdAt);

      let freed = 0;
      for (const e of entries) {
        if (freed >= requiredBytes) break;
        this.remove(e.key);
        freed += e.size;
      }
    } catch (error) {
      console.warn('[OfflineCache] Eviction failed:', error);
    }
  }

  private isQuotaExceededError(error: unknown): boolean {
    if (error instanceof DOMException) {
      return (
        error.name === 'QuotaExceededError' ||
        error.code === 22 ||
        error.code === 1014
      );
    }
    return false;
  }

  private updateMeta(key: string, size: number): void {
    try {
      const metaKey = this.metaPrefix + key;
      const meta = { size, updatedAt: Date.now() };
      localStorage.setItem(metaKey, JSON.stringify(meta));
    } catch {
      // Non-critical
    }
  }
}

/** Singleton instance for frontend use */
export const offlineCacheService = new LocalStorageOfflineCacheService();
